// @ts-expect-error TS6133 -- MIGRATION: not approved, fix when encountered
import React, { useCallback, useState, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useDeviceData, osName } from 'react-device-detect'
import {
  Heading,
  Input,
  Field,
  FormLabel,
  Paragraph,
  Button,
  PasswordVisibilityButton,
} from '@quickmd-llc/design-system'
import { setError } from '../../store/home/HomeActions'
import { login } from '../../store/home/HomeThunk'
import { errorCodes, getUserFriendlyErrorMessage } from '../../utils/errormessages'
import { validateEmail } from '../../utils/common'
import { getEnv, getVersion } from '../../utils/buildinfo'
import { ProviderPage } from '../../components/provider-page/ProviderPage'
import { WAITING_ROOM_PAGE_PATH, QUICK_MD_TITLE_TEXT } from '../../utils/constants'
import useDocumentTitle from '../../utils/hooks/useDocumentTitle'
import { useSelector } from 'store/hooks/useSelector'
import { useStytchOAuth } from 'hooks/useStytchOAuth'
import { LOGIN_SUCCESS } from 'store/home/HomeConstants'
import logo from '../../images/common/quickmd-main-logo.png'
import googleIcon from '../../images/common/google-icon.svg'

export default function Home() {
  const dispatch = useDispatch()
  // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
  const { browser } = useDeviceData(window?.userAgent)
  const navigate = useNavigate()
  // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
  const loading = useSelector((state) => state.home.loading)
  // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
  const error = useSelector((state) => state.home.error)
  // Treat "flags not yet fetched" as SSO-only so we don't flash the email/password form
  // before Firebase Remote Config resolves.
  const flagsFetched = useSelector((state) => state.flags.flagsFetched)
  // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
  const providerGoogleSso = useSelector((state) => state.flags.flags.provider_google_sso)
  const useGoogleSSO = !flagsFetched || providerGoogleSso
  const loginSuccess = useSelector((state) => state.auth.accessToken)
  const [emailValue, setEmailValue] = useState('')
  const [passwordValue, setPasswordValue] = useState('')
  const [isEmailError, setEmailError] = useState(false)
  const [isPasswordError, setPasswordError] = useState(false)
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const clientInfo = useMemo(
    () => ({
      OsType: osName,
      Type: browser.name,
      Version: browser.version,
    }),
    [browser.name, browser.version],
  )

  const { startGoogleOAuth, isVerifying } = useStytchOAuth({
    onSuccess: (response) => {
      dispatch({ type: LOGIN_SUCCESS, payload: { AccessToken: response.AccessToken } })
      navigate(WAITING_ROOM_PAGE_PATH)
    },
    onFailure: (err) => {
      console.log('OAuth authentication failed:', err)
    },
  })
  useDocumentTitle(QUICK_MD_TITLE_TEXT)

  const onLogin = useCallback(() => {
    setPasswordError(false)
    setEmailError(false)
    let isError = false
    if (!emailValue.trim() && !passwordValue.trim()) {
      // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
      dispatch(setError(errorCodes.FILL_ALL_FIELDS))
      isError = true
      setEmailError(isError)
      setPasswordError(true)
    } else {
      if (!emailValue.trim()) {
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        dispatch(setError(errorCodes.FIELD_REQUIRED, 'Email'))
        isError = true
        setEmailError(isError)
      } else if (!validateEmail(emailValue)) {
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        dispatch(setError(errorCodes.ENTER_VALID, 'email'))
        isError = true
        setEmailError(isError)
      }
      if (!passwordValue.trim()) {
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        if (!isError) dispatch(setError(errorCodes.FIELD_REQUIRED, 'Password'))
        isError = true
        setPasswordError(true)
      }
    }
    if (!isError) {
      dispatch(login({ Email: emailValue, Password: passwordValue, ClientInfo: clientInfo }))
    }
  }, [clientInfo, dispatch, emailValue, passwordValue])

  const onSubmit = useCallback(
    // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
    (e) => {
      e.preventDefault()
      onLogin()
    },
    [onLogin],
  )

  const onSetEmail = useCallback(
    // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
    (value) => {
      setEmailValue(value)
      setEmailError(false)
      if (error) dispatch(setError())
    },
    [dispatch, error],
  )
  const onSetPassword = useCallback(
    // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
    (value) => {
      setPasswordValue(value)
      setPasswordError(false)
      if (error) dispatch(setError())
    },
    [dispatch, error],
  )

  const onForgotPassword = useCallback(() => {
    if (error) dispatch(setError())
    setPasswordError(false)
    setEmailError(false)
    navigate('/forgot-password')
  }, [dispatch, error, navigate])

  if (loginSuccess) {
    navigate(WAITING_ROOM_PAGE_PATH)
  }
  const onContinueWithGoogle = () => {
    try {
      startGoogleOAuth()
    } catch (e) {
      console.error('Error during Google login:', e)
    }
  }

  const isLoginError = error === getUserFriendlyErrorMessage(errorCodes.INCORRECT_LOGIN)

  return (
    // @ts-expect-error TS2740 -- MIGRATION: not approved, fix when encountered
    <ProviderPage
      isLoginPage
      loading={loading || isVerifying}
      render={
        // TODO(QMD-3587): remove the `tailwind-scoped` wrapper once Tailwind is no longer scoped at the app level.
        <div className='tailwind-scoped' style={{ width: '100%', height: '100%' }}>
          <div className='relative flex min-h-screen w-full flex-col items-center justify-center bg-white'>
            <div className='bg-surface flex w-sp-80 max-w-[90vw] flex-col items-center gap-sp-3 rounded-large border border-border-medium px-sp-3 py-sp-4'>
              <img src={logo} alt='QuickMD Logo' className='h-auto w-[213px]' />
              <Heading level='h2' className='m-0 text-center'>
                Sign in
              </Heading>

              {!useGoogleSSO && (
                <form onSubmit={onSubmit} noValidate className='flex w-full flex-col items-center gap-sp-3'>
                  {/* Form-level error banner. Must NOT use <ErrorMessage> here: that's a
                    Headless UI <Description>, which throws unless it's inside a <Field>.
                    A standalone <Paragraph> renders the same error text without that crash. */}
                  {error ?
                    <Paragraph size='sm' error role='alert' aria-live='polite' className='w-full text-center'>
                      {error}
                    </Paragraph>
                  : null}
                  <Field className='flex w-full flex-col gap-sp-0.5' error={isEmailError || isLoginError}>
                    <FormLabel>Email</FormLabel>
                    <Input
                      type='email'
                      autoComplete='email'
                      placeholder='Enter your email'
                      value={emailValue}
                      onChange={(e) => onSetEmail(e.target.value)}
                      error={isEmailError || isLoginError}
                    />
                  </Field>
                  <Field className='flex w-full flex-col gap-sp-0.5' error={isPasswordError || isLoginError}>
                    <FormLabel>Password</FormLabel>
                    <Input
                      type={isPasswordVisible ? 'text' : 'password'}
                      autoComplete='current-password'
                      placeholder='Enter your password'
                      value={passwordValue}
                      onChange={(e) => onSetPassword(e.target.value)}
                      error={isPasswordError || isLoginError}
                      rightSideLabel={
                        <PasswordVisibilityButton
                          isVisible={isPasswordVisible}
                          onClick={() => setIsPasswordVisible((v) => !v)}
                        />
                      }
                    />
                  </Field>
                  <Button priority='primary' type='submit' className='w-full'>
                    Sign In
                  </Button>
                  <button
                    type='button'
                    onClick={onForgotPassword}
                    className='cursor-pointer border-0 bg-transparent p-0 text-sm text-text-light underline'>
                    Forgot password?
                  </button>
                </form>
              )}

              <button
                type='button'
                className="bg-surface flex w-full cursor-pointer items-center justify-center gap-sp-1 rounded-full border border-[#E7E7E7] px-sp-2 py-sp-1.5 font-['Inter'] text-sm font-semibold text-[#121212] transition-colors hover:bg-[#F0F6FF] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4285F4]"
                onClick={onContinueWithGoogle}>
                <img src={googleIcon} alt='' className='h-sp-2.5 w-sp-2.5 shrink-0' />
                Continue with Google
              </button>
            </div>
            <span className='absolute bottom-sp-3 right-sp-3 text-sm text-text-light'>
              {`v${getVersion()}(${getEnv()})`}
            </span>
          </div>
        </div>
      }
    />
  )
}
